import { useCallback, useEffect, useRef } from 'react';

import { useAtomValue } from 'jotai';

import { Unit } from '@core';
import { useResolvePendingScrubberLock, useScrubberLock, useToggle } from '@hooks';
import { useTranslate } from '@i18n';
import { useActivitySwings, useLiveAnalysis, useUpdateCurrentActivityMode } from '@queries';
import {
    activeSwingIdAtom,
    activeUserIdAtom,
    activityAtom,
    activityIdAtom,
    isBallLessModeAtom,
    isCurrentActivityAtom,
    isDefaultModeAtom,
    isLiveFeedbackModeAtom,
} from '@store';
import {
    Above,
    ActionButton,
    ActivityList,
    ActivityListBaseItem,
    Box,
    CaretLeftIcon,
    CaretRightIcon,
    Heading3,
    IconButton,
    Spinner,
    Text,
    u,
} from '@ui';

import { SwingNameModal } from '../SwingNameModal/SwingNameModal';

import * as css from './ActivityNavigation.css';
import { SwingActivity, SwingActivityProps } from './SwingActivity';
import { useKeyParameter } from './useKeyParameter';
import { useSwingNameUpdateModal } from './useSwingNameUpdateModal';

export const ACTIVITY_MODES = {
    DEFAULT: 'DEFAULT',
    BALLLESS: 'BALLLESS',
    LIVE_FEEDBACK: 'LIVE_FEEDBACK',
} as const;

type ActivityNavigationProps = {
    onEditKeyParameter?: () => void;
    onNavigateToLiveAnalysis?: () => void;
    isLiveAnalysis?: boolean;
    isNewSwing?: boolean;
    onNavigateToNewSwing?: () => void;
    onDeleteActivity?: () => void;
};

const useScrollItemIntoView = () => {
    const listRef = useRef<HTMLUListElement | null>(null);

    const activeSwingId = useAtomValue(activeSwingIdAtom);
    useEffect(() => {
        if (!activeSwingId || !listRef.current) return;
        const list = listRef.current;
        const listItem = list.querySelector<HTMLLIElement>(`[data-swing-id="${activeSwingId}"]`);

        if (!listItem) return;

        const liTop = listItem.offsetTop;
        const liBottom = liTop + listItem.offsetHeight;

        if (liTop < list.scrollTop || liBottom > list.scrollTop + list.clientHeight) {
            listItem.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
    }, [activeSwingId, listRef]);

    return { listRef };
};

const CurrentActivityMode = () => {
    const isBallLessMode = useAtomValue(isBallLessModeAtom);
    const isDefaultMode = useAtomValue(isDefaultModeAtom);
    const isLiveFeedbackMode = useAtomValue(isLiveFeedbackModeAtom);
    const { mutateAsync: updateActivityMode } = useUpdateCurrentActivityMode();
    return (
        <Box display="flex" onClick={(e) => e.stopPropagation()}>
            {isBallLessMode && (
                <ActionButton
                    icon="golf"
                    variant="plain"
                    buttonSize="medium"
                    iconSize="extraLarge"
                    remove
                    onClick={() => updateActivityMode(ACTIVITY_MODES.DEFAULT)}
                />
            )}
            {isDefaultMode && (
                <ActionButton
                    icon="golf"
                    variant="plain"
                    buttonSize="medium"
                    iconSize="extraLarge"
                    onClick={() => updateActivityMode(ACTIVITY_MODES.BALLLESS)}
                />
            )}
            {isLiveFeedbackMode && <ActionButton icon="broadcast" variant="plain" buttonSize="tiny" iconSize="small" />}
        </Box>
    );
};

export function ActivityNavigation({
    onSelectActive,
    onEditKeyParameter,
    onNavigateToLiveAnalysis,
    onNavigateToNewSwing,
    onDeleteActivity,
    isLiveAnalysis,
    isNewSwing,
}: ActivityNavigationProps & Pick<SwingActivityProps, 'onSelectActive'>) {
    const isCurrentActivity = useAtomValue(isCurrentActivityAtom);
    const activityId = useAtomValue(activityIdAtom);
    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const activeUserId = useAtomValue(activeUserIdAtom);
    const activity = useAtomValue(activityAtom);
    const { listRef } = useScrollItemIntoView();
    const { data: liveEvents = [] } = useLiveAnalysis(activityId);
    const [keyParameter, isParameterLoading, isKeyParameterRefetching] = useKeyParameter();

    const { data: swings = [] } = useActivitySwings(activityId);
    const { open, isOpen, close, submit, swingUuid } = useSwingNameUpdateModal(activityId);

    const t = useTranslate();
    useResolvePendingScrubberLock();
    const handleSelectActive = useScrubberLock(onSelectActive);

    const [visible, toggleVisible] = useToggle(true);
    const handleDeleteSwing = useCallback(() => {
        if (swings.length === 1 && onDeleteActivity) {
            onDeleteActivity();
        }
    }, [swings, onDeleteActivity]);
    return (
        <Box className={css.layout[visible ? 'visible' : 'hidden']}>
            <Box className={css.wrapper[visible ? 'visible' : 'hidden']}>
                <Above bp="md">
                    <IconButton className={css.button} onClick={toggleVisible}>
                        {visible ? <CaretLeftIcon weight="bold" /> : <CaretRightIcon weight="bold" />}
                    </IconButton>
                </Above>

                <ActivityList
                    ref={listRef}
                    title={t('activity_navigation.header')}
                    subtitle={
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" py={u(0.5)}>
                                <Text variant="h4" color="muted">
                                    #{activityId}
                                </Text>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Text variant="h4" color="primary">
                                    {keyParameter?.i18n ? t(keyParameter?.i18n) : 'Loading...'}
                                </Text>
                                {isKeyParameterRefetching ? (
                                    <Spinner size="s" />
                                ) : (
                                    <ActionButton
                                        icon="pencil"
                                        variant="plain"
                                        buttonSize="tiny"
                                        iconSize="small"
                                        isDisabled={!onEditKeyParameter || !keyParameter?.i18n}
                                        onClick={onEditKeyParameter}
                                    />
                                )}
                            </Box>
                        </Box>
                    }
                >
                    {onNavigateToLiveAnalysis && (isCurrentActivity || !!liveEvents.length) && (
                        <ActivityListBaseItem onClick={onNavigateToLiveAnalysis} isSelected={isLiveAnalysis}>
                            <Heading3>LIVE</Heading3>
                        </ActivityListBaseItem>
                    )}
                    {onNavigateToNewSwing && isCurrentActivity && (
                        <ActivityListBaseItem onClick={onNavigateToNewSwing} isSelected={isNewSwing && !activeSwingId}>
                            <Heading3>SWING</Heading3>
                            <CurrentActivityMode />
                        </ActivityListBaseItem>
                    )}
                    {!isParameterLoading ? (
                        swings.map((swing) => (
                            <SwingActivity
                                key={swing.uuid}
                                parameterName={t(keyParameter.i18n ?? '-')}
                                parameterID={keyParameter.parameter.name}
                                parameterUnit={keyParameter.parameter.unit as Unit}
                                {...swing}
                                onFavorite={() => open(swing.uuid, swing.favorite)}
                                onDelete={handleDeleteSwing}
                                onSelectActive={handleSelectActive}
                                activeSwingId={activeSwingId ?? null}
                                activeUserId={activeUserId}
                            />
                        ))
                    ) : (
                        <Spinner size="m" />
                    )}
                </ActivityList>
            </Box>

            {isOpen && swingUuid && (
                <SwingNameModal
                    isOpen={isOpen}
                    swingUuid={swingUuid}
                    setIsOpen={close}
                    onSubmit={submit}
                    clubName={activity?.club?.shortName}
                />
            )}
        </Box>
    );
}
